import { HousingFormPageHeaderCard } from '@app/components/blocks/card/HousingFormPageHeaderCard'
import { Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { useTranslations } from '@i18n/useLanguage'
import useTransform from '@app/util/useTransform'
import { Card } from '@app/components/blocks/card/Card'
import { fullName } from '@app/util/helpers'
import { TabContent } from './TabContent'
import { housingFormTabs, housingFormTabsQueryParamName } from './constants'
import { getHousingFormPageTitle } from './getPageTitle'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'
import { useParamState } from '@app/util/hooks/useParamState'
import { HousingFormPagePopularHousingFormsCard } from './sections/HousingFormPagePopularHousingFormsCard'
import { ItemListCardItem } from '@app/components/blocks/card/ItemListCard'
import { ViewHousingFormModel } from 'functions/app/housing-form/view'
import { pages } from '@app/util/links'
import { useLocation } from 'wouter'
import { DiscoverPageLayout } from '@app/pages/layout/DiscoverPageLayout'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'

export type HousingFormPageProps = {
  id: string
}

export const HousingFormPage = ({ id }: HousingFormPageProps) => {
  const t = useTranslations().app
  const transformStyle = useTransform()
  const [, setLocation] = useLocation()
  const housingFormQuery = useQuery('view-housing-form', { id })
  const housingForm: ViewHousingFormModel | undefined = housingFormQuery.data

  const excludeIds = useMemo(() => [housingForm?.id], [housingForm?.id])

  const pageStatus = getEntityPageStatus(
    housingFormQuery.loading,
    housingForm,
  )

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.housingFormPage.loading}
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="2rem">
      <PageHead housingForm={housingForm} />
      <DiscoverPageLayout
        style={transformStyle}
        sidebar={
          <>
            <HousingFormPagePopularHousingFormsCard excludeIds={excludeIds} />
            {housingForm?.ambassador && (
              <Card>
                <Title.H5>{t.housingFormPage.ambassadorCard.title}</Title.H5>
                <ItemListCardItem
                  item={{
                    image: housingForm?.ambassador?.avatar?.src || '',
                    title: fullName(housingForm?.ambassador),
                    onClick: () =>
                      setLocation(
                        pages.profile(housingForm?.ambassador?.handle),
                      ),
                  }}
                />
              </Card>
            )}
          </>
        }
      >
        <HousingFormPageHeaderCard
          style={{ flex: 1 }}
          housingForm={housingForm}
        />
        <TabContent housingForm={housingForm} />
      </DiscoverPageLayout>
    </PageLayout>
  )
}

const PageHead = ({ housingForm }: { housingForm: ViewHousingFormModel }) => {
  const translations = useTranslations().app.housingFormPage
  const [tab] = useParamState(housingFormTabs, housingFormTabsQueryParamName)
  const pageTitle = getHousingFormPageTitle(
    tab,
    housingForm.title,
    translations.pageTabTitle,
  )
  return (
    <PageSeo
      pageTitle={pageTitle}
      description={housingForm.description}
      image={housingForm.images?.[0]}
      noindex={shouldNoindexPageTab(tab)}
    />
  )
}
